import { union } from 'es-toolkit';

import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getDeployment } from '@/redux/modules/config';
import { getSellers, getSellersByIds } from '@/redux/modules/seller/seller.api';
import { shouldFetchSellers, whichSellersNeeded } from '@/redux/modules/seller/seller.selectors';
import {
    LOAD_SELLERS_FAIL,
    LoadSellersFailAction,
    LOAD_SELLERS_REQUEST,
    LoadSellersRequestAction,
    LOAD_SELLERS_SUCCESS,
    LoadSellersSuccessAction,
    SellerResponseData,
} from '@/redux/modules/seller/seller.types';
import { whichSellerCatalogCountsNeeded } from '@/redux/modules/sellerCatalogCounts';
import { Seller } from '@/types/Seller';

export const fetchAllSellers = createAsyncThunk<Seller[], void>(
    'la/ui/seller/fetchAllSellers',
    async (_, { getState }) => {
        const state = getState();
        const deployment = getDeployment(state);

        const response = await getSellers({ deployment });
        return response.data;
    },
    {
        condition: (_, { getState }) => shouldFetchSellers(getState()),
    }
);

export const fetchSellersByIds = createAsyncThunk<SellerResponseData, number[], AsyncThunkConfigWithRejectValue>(
    'la/ui/seller/fetchSellersByIds',
    async (sellerIds, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);

            // If sellerCatalogCounts is converted to toolkit, this action can be removed
            // (also the success and fail actions)
            const requestAction: LoadSellersRequestAction = {
                error: false,
                type: LOAD_SELLERS_REQUEST,
                payload: sellerIds,
            };
            dispatch(requestAction);

            const response = await getSellersByIds({
                deployment,
                sellerIds,
            });

            // If sellerCatalogCounts is converted to toolkit, this action can be removed
            // (also the request and fail actions)
            const successAction: LoadSellersSuccessAction = {
                error: false,
                meta: {
                    actionTime: Date.now(),
                    sellerIds,
                },
                payload: response.data,
                type: LOAD_SELLERS_SUCCESS,
            };
            dispatch(successAction);

            return response.data;
        } catch (error) {
            // If sellerCatalogCounts is converted to toolkit, this action can be removed
            // (also the request and success actions)
            const failAction: LoadSellersFailAction = {
                error: true,
                meta: {
                    sellerIds,
                },
                payload: error,
                type: LOAD_SELLERS_FAIL,
            };
            dispatch(failAction);

            const publishedError = typeof error === 'string' ? new Error(error) : (error as Error);
            return rejectWithValue(publishedError);
        }
    }
);

export const fetchSellersByIdsIfNeeded = createAsyncThunk<void, number[]>(
    'la/ui/seller/fetchSellersByIdsIfNeeded',
    async (sellerIds, { dispatch, getState }) => {
        const neededSellers = whichSellersNeeded(getState(), sellerIds);
        const neededCatalogCounts = whichSellerCatalogCountsNeeded(getState(), sellerIds);
        const needed = union(neededSellers, neededCatalogCounts);

        if (needed.length) {
            await dispatch(fetchSellersByIds(needed));
        }
    }
);
